import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase, signInWithProvider } from '../../lib/auth';
import { useAuth } from '../../components/AuthProvider';
import { homeForProfile } from '../../lib/refonteNav';

// Après une première connexion par code : comment se connecter la prochaine fois ?
// Google (rattaché au même email), un mot de passe, ou rien (le code marche à chaque fois).
const SetupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const reason = ((location.state as any) || {}).reason as string | undefined;
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [showForm, setShowForm] = useState(reason === 'forgot');
  const [busy, setBusy] = useState<'pw' | 'google' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const home = () => navigate(homeForProfile(profile), { replace: true });

  const validate = (p: string) => {
    if (p.length < 8) return 'Au moins 8 caractères.';
    if (!/[a-z]/.test(p) || !/[A-Z]/.test(p) || !/\d/.test(p)) return 'Il faut une majuscule, une minuscule et un chiffre.';
    return null;
  };

  const savePw = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    const v = validate(pw); if (v) { setError(v); return; }
    if (pw !== pw2) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    setBusy('pw');
    try {
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) throw error;
      setDone(true);
      setTimeout(home, 1200);
    } catch (err: any) { setError(err?.message || 'Erreur lors de l\'enregistrement.'); setBusy(null); }
  };

  const google = async () => {
    setError(null); setBusy('google');
    try { await signInWithProvider('google'); }
    catch (err: any) { setError(err?.message || 'Erreur Google'); setBusy(null); }
  };

  const input = 'w-full rounded-lg border border-line-2 bg-panel px-3 py-2.5 text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose';

  return (
    <div className="refonte-scope min-h-screen flex flex-col justify-center px-4 py-10 bg-cream text-ink">
      <div className="w-full max-w-[420px] mx-auto">
        <div className="text-center mb-8"><img src="/hook-logo.png" alt="Hook" className="h-16 w-auto mx-auto" /></div>
        <div className="bg-panel border border-line rounded-card p-6 sm:p-8 shadow-sm">
          {done ? (
            <div className="text-center py-4">
              <CheckCircle className="h-12 w-12 text-st-ok mx-auto mb-3" />
              <h2 className="text-[17px] font-bold mb-1">Mot de passe enregistré</h2>
              <p className="text-dim text-[13px]">Direction ton espace…</p>
            </div>
          ) : (
            <>
              <h2 className="text-center text-[20px] font-extrabold mb-1">
                {reason === 'forgot' ? 'Nouveau mot de passe' : 'Bienvenue dans l\'univers Hook 👋'}
              </h2>
              <p className="text-center text-dim text-[13px] mb-6">
                {reason === 'forgot' ? 'Choisis ton nouveau mot de passe.' : 'Tu es connecté. Comment veux-tu te connecter la prochaine fois ?'}
              </p>

              {error && (
                <div className="mb-4 text-[12.5px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />{error}
                </div>
              )}

              {reason !== 'forgot' && (
                <>
                  <button onClick={google} disabled={!!busy}
                    className="w-full flex items-center justify-center gap-2.5 text-[13.5px] font-semibold px-4 py-2.5 rounded-lg border border-line-2 bg-panel hover:border-ink disabled:opacity-50">
                    {busy === 'google' ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="font-bold text-[15px]">G</span>}
                    Avec Google (recommandé)
                  </button>
                  <div className="flex items-center gap-3 my-5">
                    <span className="h-px bg-line flex-1" /><span className="font-mono text-[10px] uppercase tracking-widest text-faint">ou</span><span className="h-px bg-line flex-1" />
                  </div>
                </>
              )}

              {!showForm ? (
                <button onClick={() => setShowForm(true)} className="w-full text-[13px] font-semibold px-4 py-2.5 rounded-lg bg-ink text-white hover:opacity-90">
                  Créer un mot de passe
                </button>
              ) : (
                <form className="space-y-3" onSubmit={savePw}>
                  <div className="relative">
                    <input type={show ? 'text' : 'password'} required autoFocus autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)}
                      placeholder="Mot de passe (8+ car., Maj/min/chiffre)" className={`${input} pr-10`} />
                    <button type="button" onClick={() => setShow(v => !v)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-faint">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <input type={show ? 'text' : 'password'} required autoComplete="new-password" value={pw2} onChange={e => setPw2(e.target.value)}
                    placeholder="Confirme le mot de passe" className={input} />
                  <button type="submit" disabled={busy === 'pw' || !pw || !pw2}
                    className="w-full text-[13.5px] font-semibold px-4 py-2.5 rounded-lg bg-hook-rose text-white hover:opacity-90 disabled:opacity-50">
                    {busy === 'pw' ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </form>
              )}

              <p className="text-center mt-5 text-[12px]">
                <button onClick={home} className="text-dim hover:text-ink underline">
                  Plus tard — je me connecterai par code
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupPage;
